import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Las mismas reglas de siempre y en el mismo orden: nombre, teléfono, dirección y parentesco
// no pueden ir vacíos. Se devuelven todas juntas, cada una con la clave de su campo.
export const validarReferencia = ({ fullName, telefono, direccion, parentesco }) => {
  const errores = {};
  if (fullName === "") errores.fullName = "Ingrese los nombres y apellidos";
  if (telefono === "") errores.telefono = "Ingrese un número telefónico";
  if (direccion === "") errores.direccion = "Ingrese una dirección";
  if (parentesco === "") errores.parentesco = "Indique el parentesco con el cliente";
  return errores;
};

/**
 * Los cuatro campos de una referencia personal. Solo presentación: el estado y la llamada a la
 * API viven en el diálogo que lo usa.
 *
 * @param {object} props
 * @param {string} props.prefijo Para los ids de los campos.
 * @param {{fullName: string, telefono: string, direccion: string, parentesco: string}} props.valores
 * @param {Object<string, string>} props.errores
 * @param {(campo: string, valor: string) => void} props.onCambio
 * @param {boolean} [props.deshabilitado]
 */
export const CamposReferencia = ({ prefijo, valores, errores, onCambio, deshabilitado }) => (
  <Stack spacing={2.5} sx={{ pt: 1 }}>
    <TextField
      required
      fullWidth
      autoFocus
      id={`${prefijo}-nombre`}
      label="Nombres y apellidos"
      value={valores.fullName ?? ""}
      onChange={(e) => onCambio("fullName", e.target.value.toUpperCase())}
      error={Boolean(errores.fullName)}
      helperText={errores.fullName || " "}
      disabled={deshabilitado}
      inputProps={{ autoComplete: "off" }}
    />
    <TextField
      required
      fullWidth
      id={`${prefijo}-telefono`}
      label="Teléfono"
      value={valores.telefono ?? ""}
      onChange={(e) => onCambio("telefono", e.target.value)}
      error={Boolean(errores.telefono)}
      helperText={errores.telefono || "Solo números"}
      disabled={deshabilitado}
      inputProps={{ inputMode: "numeric", autoComplete: "off" }}
    />
    <TextField
      required
      multiline
      fullWidth
      id={`${prefijo}-direccion`}
      label="Dirección"
      value={valores.direccion ?? ""}
      onChange={(e) => onCambio("direccion", e.target.value.toUpperCase())}
      error={Boolean(errores.direccion)}
      helperText={errores.direccion || " "}
      disabled={deshabilitado}
      inputProps={{ autoComplete: "off" }}
    />
    <TextField
      required
      fullWidth
      id={`${prefijo}-parentesco`}
      label="Parentesco"
      value={valores.parentesco ?? ""}
      onChange={(e) => onCambio("parentesco", e.target.value.toUpperCase())}
      error={Boolean(errores.parentesco)}
      helperText={errores.parentesco || "Por ejemplo: HERMANO, VECINO, AMIGO"}
      disabled={deshabilitado}
      inputProps={{ autoComplete: "off" }}
    />
  </Stack>
);

/**
 * Diálogo para agregar o cambiar una referencia. Se adapta a la pantalla (con margen en el
 * teléfono) y no se cierra con un clic fuera ni con Escape si hay algo escrito (`sucio`), ni
 * mientras guarda: así no se pierde lo escrito.
 *
 * @param {object} props
 * @param {string} props.idTitulo Id del título, para `aria-labelledby`.
 * @param {string} props.titulo
 * @param {string} props.textoGuardar
 * @param {boolean} props.guardando
 * @param {boolean} props.sucio Hay cambios sin guardar.
 * @param {() => void} props.onGuardar
 * @param {() => void} props.onCerrar
 */
export const DialogoReferencia = ({
  idTitulo,
  titulo,
  textoGuardar,
  guardando,
  sucio,
  onGuardar,
  onCerrar,
  children,
}) => {
  const cerrar = (_evento, razon) => {
    if (guardando) return;
    if ((razon === "backdropClick" || razon === "escapeKeyDown") && sucio) return;
    onCerrar();
  };

  return (
    <Dialog
      open
      onClose={cerrar}
      fullWidth
      maxWidth="sm"
      aria-labelledby={idTitulo}
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 1.5, sm: 4 },
          maxWidth: { xs: "calc(100% - 24px)", sm: 520 },
        },
      }}
    >
      <DialogTitle
        id={idTitulo}
        component="h2"
        sx={{ fontWeight: 700, color: "primary.main", pr: 7 }}
      >
        {titulo}
      </DialogTitle>
      <IconButton
        aria-label="Cerrar"
        onClick={() => cerrar()}
        disabled={guardando}
        sx={{
          position: "absolute",
          top: (theme) => theme.spacing(1.5),
          right: (theme) => theme.spacing(1.5),
          color: "text.secondary",
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => cerrar()} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={guardando}
          onClick={onGuardar}
          startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {guardando ? "Guardando…" : textoGuardar}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
